import { z } from "zod";
// ─────────────────────────────────────────────────────────────────────────────
// Piste V10 — Comptes Professionnels (docs/PRO_ACCOUNTS_SPEC.md)
// Schémas partagés client/serveur.
//
// RÈGLE US-125 (cloison données perso) : aucun DTO exposé à un pro ne contient
// l'email ni le téléphone d'un client — uniquement userId, displayName, handle.
// ─────────────────────────────────────────────────────────────────────────────
// ── Enums (miroir des enums Prisma) ──────────────────────────────────────────
export const BusinessStatusSchema = z.enum([
    "PENDING_VERIFICATION",
    "ACTIVE",
    "SUSPENDED",
]);
export const BusinessCategorySchema = z.enum([
    "COIFFURE",
    "BARBIER",
    "BEAUTE_ONGLES",
    "TATOUAGE",
    "SPORT_COACHING",
    "EQUITATION",
    "BIEN_ETRE",
    "AUTRE",
]);
export const BusinessPlanSchema = z.enum(["FREE", "PRO"]);
export const ExceptionKindSchema = z.enum(["CLOSED", "EXTRA_OPEN"]);
export const BookingStatusSchema = z.enum([
    "REQUESTED",
    "CONFIRMED",
    "DECLINED",
    "RESCHEDULE_PROPOSED",
    "CANCELLED_CLIENT",
    "CANCELLED_PRO",
    "COMPLETED",
    "NO_SHOW",
    // US-102/103 (phase 3) — en attente de prépaiement. Jamais émis flag OFF.
    "PENDING_PAYMENT",
]);
// ── Briques communes ─────────────────────────────────────────────────────────
/** SIRET : exactement 14 chiffres (validation Sirene côté serveur, D3/US-11). */
export const SiretSchema = z.string().regex(/^\d{14}$/, "SIRET must be 14 digits");
/** Adresse du point de vente (pré-remplie via Sirene, géocodée dès l'inscription). */
export const BusinessAddressSchema = z.object({
    line1: z.string().min(1),
    line2: z.string().optional(),
    postalCode: z.string().min(1),
    city: z.string().min(1),
    country: z.string().default("FR"),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
});
// ── Modules pro (US-R21/R22/R23) ─────────────────────────────────────────────
// Déclarés avant le DTO de profil, qui les référence.
/**
 * Ce que le pro choisit d'exposer. Un module = une INTENTION (« je vends des
 * produits ») ; `ShopSettings.active` = une PUBLICATION (« ma boutique est en
 * ligne »). Deux notions distinctes qui coexistent sans se contredire.
 */
export const ProModulesSchema = z.object({
    /** Mini-page publique + présence dans les listings. */
    publicPage: z.boolean(),
    /** Carte de prestations (tarifs) — publique même sans réservation. */
    services: z.boolean(),
    /** Réservation en ligne. Implique `services` (cf. normalizeProModules). */
    agenda: z.boolean(),
    /** Boutique de produits. */
    shop: z.boolean(),
});
/** Patch partiel des modules (US-R22) : le client n'envoie que ce qu'il change. */
export const UpdateProModulesInputSchema = ProModulesSchema.partial();
/** Comportement historique : tout allumé. Sert de défaut à la création et de
 *  repli client quand `modules` est absent (instance N-1 pendant un rolling). */
export const ALL_PRO_MODULES_ON = {
    publicPage: true,
    services: true,
    agenda: true,
    shop: true,
};
/**
 * Règle structurante — **Agenda ⇒ Prestations**.
 *
 * Ce n'est pas une préférence d'UI mais une propriété du modèle : `Booking.serviceId`
 * est NOT NULL, `computeFreeSlots` retourne `null` sans service, et la durée d'un
 * créneau EST `ProService.durationMin`. Un agenda sans prestation est
 * mathématiquement incapable de produire un créneau — l'activer promettrait au
 * visiteur une réservation impossible.
 *
 * L'implication ne vaut que dans ce sens : « Prestations » seul reste valide,
 * c'est une carte de tarifs publique sans réservation.
 *
 * Partagée client/serveur pour que l'UI grise exactement ce que le serveur refuse.
 */
export function normalizeProModules(modules) {
    return { ...modules, agenda: modules.agenda && modules.services };
}
/** US-R21 : au moins un module doit rester actif — un espace pro entièrement
 *  éteint n'est pas un espace pro, c'est un compte perso. */
export function hasAnyProModule(modules) {
    return modules.publicPage || modules.services || modules.agenda || modules.shop;
}
// ── Profil pro ───────────────────────────────────────────────────────────────
/**
 * DTO public du profil pro (mini-page, D8).
 * Le SIRET complet n'est JAMAIS exposé côté public — seulement `siretVerified`.
 * `handle` = User.handle (D2 : même namespace de @, pas de champ dédié).
 */
export const BusinessProfileDTOSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid(),
    handle: z.string().nullable(),
    /** US-R21 — ce que le pro expose. Toujours servi par l'instance N ; un client
     *  qui parlerait encore à une instance N-1 doit retomber sur
     *  `ALL_PRO_MODULES_ON` (= le comportement historique). */
    modules: ProModulesSchema,
    displayName: z.string(),
    legalName: z.string(),
    siretVerified: z.boolean(),
    status: BusinessStatusSchema,
    category: BusinessCategorySchema,
    phone: z.string(),
    address: BusinessAddressSchema,
    googlePlaceId: z.string().nullable(),
    bio: z.string().nullable(),
    logoKey: z.string().nullable(),
    plan: BusinessPlanSchema,
    createdAt: z.string().datetime(),
});
/** Création du compte pro (US-11) — le handle est géré via le User existant (D2). */
export const CreateBusinessProfileInputSchema = z.object({
    legalName: z.string().trim().min(2).max(200),
    siret: SiretSchema,
    category: BusinessCategorySchema,
    phone: z.string().trim().min(6).max(20),
    address: BusinessAddressSchema,
    googlePlaceId: z.string().trim().min(1).optional(),
    bio: z.string().trim().max(500).optional(),
    /** US-R21 — 3e étape d'onboarding. Absent = tout activé : un client N-1 qui
     *  ignore les modules crée un pro au comportement historique. */
    modules: ProModulesSchema.optional(),
});
/** Édition du profil pro (US-13) — le SIRET et la raison sociale ne sont pas éditables ici. */
export const UpdateBusinessProfileInputSchema = z.object({
    phone: z.string().trim().min(6).max(20).optional(),
    address: BusinessAddressSchema.optional(),
    googlePlaceId: z.string().trim().min(1).nullable().optional(),
    bio: z.string().trim().max(500).nullable().optional(),
    logoKey: z.string().trim().min(1).nullable().optional(),
    /** US-R22 — patch partiel, fusionné côté serveur avec l'état courant. */
    modules: UpdateProModulesInputSchema.optional(),
});
// ── Catalogue de services ────────────────────────────────────────────────────
/** Option de prestation : supplément durée et/ou prix ("barbe +10min +8€"). */
export const ProServiceOptionSchema = z.object({
    name: z.string().trim().min(1).max(100),
    extraDurationMin: z.number().int().min(0).max(600).default(0),
    extraPriceCents: z.number().int().min(0).default(0),
});
export const ProServiceDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    name: z.string(),
    durationMin: z.number().int().positive(),
    priceCents: z.number().int().nonnegative().nullable(),
    options: z.array(ProServiceOptionSchema).nullable(),
    capacity: z.number().int().positive(),
    rebookAfterWeeks: z.number().int().positive().nullable(),
    /** US-102 — prépaiement exigé pour CETTE prestation (inerte flag OFF). */
    requirePrepay: z.boolean(),
    active: z.boolean(),
});
export const CreateProServiceInputSchema = z.object({
    name: z.string().trim().min(1).max(100),
    durationMin: z.number().int().min(5).max(600),
    priceCents: z.number().int().min(0).optional(),
    options: z.array(ProServiceOptionSchema).max(20).optional(),
    capacity: z.number().int().min(1).max(100).default(1),
    rebookAfterWeeks: z.number().int().min(1).max(52).optional(),
    /** US-102 — prépaiement exigé pour cette prestation (inerte flag OFF). */
    requirePrepay: z.boolean().default(false),
});
export const UpdateProServiceInputSchema = CreateProServiceInputSchema.partial().extend({
    active: z.boolean().optional(),
});
// ── Disponibilités ───────────────────────────────────────────────────────────
/** Récurrence hebdo (US-31) : weekday 0-6, minutes depuis minuit. */
export const AvailabilityRuleInputSchema = z
    .object({
    weekday: z.number().int().min(0).max(6),
    startMin: z.number().int().min(0).max(1439),
    endMin: z.number().int().min(1).max(1440),
    slotEvery: z.number().int().min(5).max(480).optional(),
    maxPerDay: z.number().int().min(1).max(100).optional(),
})
    .refine((r) => r.endMin > r.startMin, {
    message: "endMin must be greater than startMin",
    path: ["endMin"],
});
export const AvailabilityRuleDTOSchema = z.object({
    id: z.string().uuid(),
    workerId: z.string().uuid(),
    weekday: z.number().int().min(0).max(6),
    startMin: z.number().int(),
    endMin: z.number().int(),
    slotEvery: z.number().int().nullable(),
    maxPerDay: z.number().int().nullable(),
});
/** Exception ponctuelle (US-32) : fermeture / congés / ouverture exceptionnelle. */
export const AvailabilityExceptionInputSchema = z
    .object({
    date: z.string().date(),
    kind: ExceptionKindSchema,
    startMin: z.number().int().min(0).max(1439).optional(),
    endMin: z.number().int().min(1).max(1440).optional(),
})
    .refine((e) => e.startMin === undefined || e.endMin === undefined || e.endMin > e.startMin, { message: "endMin must be greater than startMin", path: ["endMin"] });
export const AvailabilityExceptionDTOSchema = z.object({
    id: z.string().uuid(),
    workerId: z.string().uuid(),
    date: z.string().datetime(),
    kind: ExceptionKindSchema,
    startMin: z.number().int().nullable(),
    endMin: z.number().int().nullable(),
});
// ── Canevas de règles (US-53) ────────────────────────────────────────────────
/** DTO du canevas de règles du pro (BookingPolicy, champs phase MVP). */
export const BookingPolicyDTOSchema = z.object({
    autoApprove: z.boolean(),
    cancelMinHours: z.number().int().nonnegative(),
    rescheduleMinHours: z.number().int().nonnegative(),
    maxAdvanceDays: z.number().int().positive(),
    minNoticeHours: z.number().int().nonnegative(),
    /** US-46 : le pro peut désactiver la liste d'attente. */
    waitlistEnabled: z.boolean(),
    /** US-102 — prépaiement exigé pour TOUTES les prestations (inerte flag OFF). */
    requirePrepay: z.boolean(),
    /** US-104 — paliers de remboursement (affichés au client AVANT paiement). */
    refundFullUntilHours: z.number().int().nonnegative(),
    refundPartialUntilHours: z.number().int().nonnegative(),
    refundPartialPercent: z.number().int().min(0).max(100),
});
/**
 * Édition partielle du canevas (US-53) — bornes raisonnables :
 * annulation/décalage ≤ 30 jours, horizon 1-365 jours, préavis ≤ 14 jours.
 */
export const UpdateBookingPolicyInputSchema = z
    .object({
    autoApprove: z.boolean().optional(),
    cancelMinHours: z.number().int().min(0).max(720).optional(),
    rescheduleMinHours: z.number().int().min(0).max(720).optional(),
    maxAdvanceDays: z.number().int().min(1).max(365).optional(),
    minNoticeHours: z.number().int().min(0).max(336).optional(),
    /** US-46 : activer/désactiver la liste d'attente. */
    waitlistEnabled: z.boolean().optional(),
    /** US-102 — prépaiement exigé par politique (inerte flag OFF). */
    requirePrepay: z.boolean().optional(),
    /** US-104 — paliers de remboursement, modifiables par le pro. */
    refundFullUntilHours: z.number().int().min(0).max(720).optional(),
    refundPartialUntilHours: z.number().int().min(0).max(720).optional(),
    refundPartialPercent: z.number().int().min(0).max(100).optional(),
})
    .refine((p) => Object.keys(p).length > 0, { message: "empty patch" });
// ── Créneaux & réservation ───────────────────────────────────────────────────
/** Créneau libre calculé (US-41). `remaining` = places restantes (capacité, US-23). */
export const SlotDTOSchema = z.object({
    workerId: z.string().uuid(),
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    remaining: z.number().int().nonnegative(),
});
/**
 * Demande de RDV (US-42) — envoyée sur POST /public/pro/:handle/bookings.
 * Le business est dérivé du handle côté serveur (jamais du body).
 */
export const CreateBookingInputSchema = z.object({
    workerId: z.string().uuid(),
    serviceId: z.string().uuid(),
    startAt: z.string().datetime(),
    /** Noms des options choisies parmi ProService.options. */
    options: z.array(z.string().trim().min(1)).max(20).optional(),
});
/** Décalage d'un RDV par le client (US-43) — POST /me/bookings/:id/reschedule. */
export const RescheduleBookingInputSchema = z.object({
    newStartAt: z.string().datetime(),
});
/**
 * Identité d'un client telle qu'exposée au pro (US-125) :
 * userId + displayName + handle UNIQUEMENT — jamais email ni téléphone.
 */
export const BookingClientDTOSchema = z.object({
    userId: z.string().uuid(),
    displayName: z.string(),
    handle: z.string().nullable(),
});
export const BookingDTOSchema = z.object({
    id: z.string().uuid(),
    businessId: z.string().uuid(),
    workerId: z.string().uuid(),
    serviceId: z.string().uuid(),
    /** Côté pro, l'identité du client est limitée à ce sous-objet (US-125). */
    client: BookingClientDTOSchema,
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    status: BookingStatusSchema,
    priceCents: z.number().int().nonnegative().nullable(),
    sessionId: z.string().uuid().nullable(),
    createdAt: z.string().datetime(),
});
//# sourceMappingURL=pro.js.map